import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface Sponsor {
  tier: "Platinum" | "Gold" | "Silver";
  name: string;
  logo?: string;
  url?: string;
  logoFilter?: string;
}

const SPONSORS: Sponsor[] = [
  { tier: "Platinum", name: "Platinum Sponsor" },
  { tier: "Platinum", name: "Platinum Sponsor" },
  { tier: "Gold", name: "Gold Sponsor" },
  { tier: "Gold", name: "Gold Sponsor" },
  { tier: "Gold", name: "Gold Sponsor" },
  {
    tier: "Silver",
    name: "Circle City Coders",
    logo: "https://c3store.nyc3.digitaloceanspaces.com/3c_v2/3C_logo_white_wtext.png",
    url: "https://circlecitycoders.com",
  },
  {
    tier: "Silver",
    name: "Kan-Kan Cinema",
    logo: "https://c3store.nyc3.cdn.digitaloceanspaces.com/mwff/KanKan%20Cinema.png",
    url: "https://kankanindy.com/",
    logoFilter: "invert(1) brightness(200%)",
  },
  {
    tier: "Silver",
    name: "Graphite Customs",
    logo: "https://c3store.nyc3.cdn.digitaloceanspaces.com/mwff/graphite-customs.png",
    url: "https://www.graphitecustoms.com/",
  },
  { tier: "Silver", name: "Silver Sponsor" },
];

const tierStyles: Record<
  string,
  { borderColor: string; labelColor: string; size: string }
> = {
  Platinum: {
    borderColor: "rgba(229,228,226,0.25)",
    labelColor: "#e5e4e2",
    size: "280px",
  },
  Gold: {
    borderColor: "rgba(255,193,7,0.2)",
    labelColor: "#ffc107",
    size: "220px",
  },
  Silver: {
    borderColor: "rgba(192,192,192,0.2)",
    labelColor: "#c0c0c0",
    size: "180px",
  },
};

export default function Sponsors() {
  const ref = useScrollAnimation();

  const platinum = SPONSORS.filter((s) => s.tier === "Platinum");
  const gold = SPONSORS.filter((s) => s.tier === "Gold");
  const silver = SPONSORS.filter((s) => s.tier === "Silver");

  const renderRow = (items: Sponsor[], delay = 0) =>
    items.map((sponsor, i) => {
      const style = tierStyles[sponsor.tier];
      const Tag = sponsor.url ? "a" : "div";
      const linkProps = sponsor.url
        ? { href: sponsor.url, target: "_blank", rel: "noopener noreferrer" }
        : {};
      return (
        <Tag
          key={`${sponsor.tier}-${i}`}
          className="glass-card animate-on-scroll"
          data-tooltip={sponsor.name}
          {...linkProps}
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            padding: "32px 24px",
            minWidth: style.size,
            minHeight: "120px",
            textAlign: "center",
            borderColor: style.borderColor,
            flex: "1 1 auto",
            transitionDelay: `${delay + i * 80}ms`,
            textDecoration: "none",
            cursor: sponsor.url ? "pointer" : "default",
          }}
        >
          <div
            style={{
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.65rem",
              letterSpacing: "0.25em",
              color: style.labelColor,
              textTransform: "uppercase",
            }}
          >
            {sponsor.tier} Sponsor
          </div>
          {sponsor.logo ? (
            <img
              src={sponsor.logo}
              alt={sponsor.name}
              style={{
                maxWidth: "80%",
                maxHeight: "56px",
                objectFit: "contain",
                opacity: 1,
                filter: sponsor.logoFilter,
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: '"Bebas Neue", cursive',
                fontSize: "1.1rem",
                letterSpacing: "0.1em",
                color: "rgba(232,232,232,0.35)",
              }}
            >
              Your Brand Here
            </div>
          )}
        </Tag>
      );
    });

  return (
    <>
    <section
      id="sponsors"
      style={{
        background: "#0a0a0f",
        padding: "clamp(64px,10vw,120px) clamp(20px,5vw,80px)",
      }}
    >
      <div ref={ref} style={{ maxWidth: "1280px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "56px", textAlign: "center" }}>
          <div
            className="section-label animate-on-scroll"
            style={{ justifyContent: "center", marginBottom: "16px" }}
          >
            Proud Partners
          </div>
          <h2
            className="display-heading animate-on-scroll"
            style={{
              fontSize: "clamp(2.5rem,6vw,4.5rem)",
              color: "#e8e8e8",
              transitionDelay: "100ms",
            }}
          >
            Our <span style={{ color: "#cc0000" }}>Sponsors</span>
          </h2>
          <p
            className="animate-on-scroll"
            style={{
              color: "rgba(232,232,232,0.55)",
              fontSize: "0.9rem",
              marginTop: "12px",
              transitionDelay: "150ms",
            }}
          >
            Interested in sponsoring? Contact us at{" "}
            <a
              href="mailto:midwestfearfest@gmail.com"
              style={{ color: "#cc0000" }}
            >
              midwestfearfest@gmail.com
            </a>
          </p>
        </div>

        {/* Platinum Row */}
        <div style={{ marginBottom: "12px" }}>
          <div
            style={{
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.65rem",
              letterSpacing: "0.3em",
              color: "#e5e4e2",
              textAlign: "center",
              marginBottom: "16px",
              opacity: 0.6,
            }}
          >
            Platinum
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {renderRow(platinum, 0)}
          </div>
        </div>

        {/* Gold Row */}
        <div style={{ marginBottom: "12px", marginTop: "32px" }}>
          <div
            style={{
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.65rem",
              letterSpacing: "0.3em",
              color: "#ffc107",
              textAlign: "center",
              marginBottom: "16px",
              opacity: 0.6,
            }}
          >
            Gold
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {renderRow(gold, 200)}
          </div>
        </div>

        {/* Silver Row */}
        <div style={{ marginTop: "32px" }}>
          <div
            style={{
              fontFamily: '"Bebas Neue", cursive',
              fontSize: "0.65rem",
              letterSpacing: "0.3em",
              color: "#c0c0c0",
              textAlign: "center",
              marginBottom: "16px",
              opacity: 0.6,
            }}
          >
            Silver
          </div>
          <div
            style={{
              display: "flex",
              gap: "16px",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {renderRow(silver, 400)}
          </div>
        </div>
      </div>
    </section>

    <style>{`
      [data-tooltip] {
        position: relative;
      }
      [data-tooltip]::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 8px);
        left: 50%;
        transform: translateX(-50%) translateY(4px);
        background: rgba(10, 10, 15, 0.92);
        border: 1px solid rgba(255,255,255,0.1);
        color: #e8e8e8;
        font-family: "Bebas Neue", cursive;
        font-size: 0.75rem;
        letter-spacing: 0.15em;
        white-space: nowrap;
        padding: 6px 12px;
        border-radius: 6px;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.18s ease, transform 0.18s ease;
        z-index: 10;
      }
      [data-tooltip]:hover::after {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
      }
    `}</style>
    </>
  );
}
